"use client";

type Tone = "auto" | "light";

/**
 * Vinyl record artwork used as an image placeholder/loader.
 *
 * The disc is mostly radially symmetric, so the off-centre specular gleam is
 * what makes rotation visible — without it a spinning disc looks frozen.
 *
 * `tone="light"` switches the disc/gleam to white for use on dark overlays
 * (e.g. the zoom modal) where the foreground colour would vanish.
 */
export function VinylIcon({ className, tone = "auto" }: { className?: string; tone?: Tone }) {
  const toneClass = tone === "light" ? "text-white" : "text-foreground";
  return (
    <svg
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`${toneClass} ${className ?? ""}`}
    >
      {/* Disc */}
      <circle cx="60" cy="60" r="56" fill="currentColor" fillOpacity={0.1} />
      {/* Grooves */}
      <circle
        cx="60"
        cy="60"
        r="48"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.08}
        strokeWidth={0.5}
      />
      <circle
        cx="60"
        cy="60"
        r="42"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.08}
        strokeWidth={0.5}
      />
      <circle
        cx="60"
        cy="60"
        r="36"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.08}
        strokeWidth={0.5}
      />
      <circle
        cx="60"
        cy="60"
        r="30"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.08}
        strokeWidth={0.5}
      />
      {/* Specular gleam — asymmetric so the disc visibly rotates */}
      <path
        d="M 20.2 37 A 46 46 0 0 1 48.1 15.6"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.4}
        strokeWidth={4}
        strokeLinecap="round"
      />
      <path
        d="M 28.9 38.2 A 38 38 0 0 1 47 24.3"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.22}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      {/* Label */}
      <circle cx="60" cy="60" r="20" fill="currentColor" className="text-primary/35" />
      {/* Center hole */}
      <circle cx="60" cy="60" r="3" fill="currentColor" fillOpacity={0.55} />
    </svg>
  );
}

/** Spinning vinyl disc — the image loading indicator. */
export function VinylSpinner({ className, tone = "auto" }: { className?: string; tone?: Tone }) {
  return <VinylIcon tone={tone} className={`animate-vinyl-spin ${className ?? ""}`} />;
}
